import { useContext, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  AiPlan,
  applyRestartRoute,
  getAiPlans,
  getRestartRoutePreview,
  RestartRouteCandidate,
  RestartRouteChange,
  RestartRoutePreview as RestartRoutePreviewData,
  undoRestartRoute,
} from "@chronospark/shared";
import MDButton from "components/MDButton";
import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";

type Stage = "plans" | "select" | "proposal";

function isEnglish(language: string) {
  return String(language).toLowerCase().startsWith("en");
}

function copyFor(language: string) {
  const en = isEnglish(language);
  return {
    title: en ? "Adjust this month's plan" : "今月の予定を調整する",
    description: en
      ? "Review only this month's unfinished weekly tasks. Daily and monthly tasks stay unchanged."
      : "今月の未完了の週次タスクだけを見直します。日次・月次タスクと完了記録は変更しません。",
    open: en ? "Adjust this month" : "予定を調整する",
    modalTitle: en ? "Adjust this month's plan" : "今月の予定を調整する",
    modalDescription: en
      ? "Choose tasks that may move later. The proposed schedule is fixed until you choose tasks again."
      : "後ろの週へ移動してよいタスクを選びます。提案を作った後は、選び直すまで提案内容は固定です。",
    choosePlan: en ? "Choose a plan" : "計画を選ぶ",
    inspect: en ? "Review eligible tasks" : "対象タスクを確認する",
    selectionTitle: en ? "Tasks that may move later" : "日程を動かしてよいタスク",
    selectionHelp: en
      ? "On: this task may move to a later week. Off: it remains on its current schedule and uses that week's capacity."
      : "チェックONのタスクだけを、現在の週より後ろへ移動できます。OFFのタスクは現在の日程のまま固定され、その週の枠を使います。",
    movable: en ? "May move to a later week" : "後ろの週へ移動してよい",
    fixed: en ? "Keep this task on its current schedule" : "このタスクは現在の日程のままにする",
    create: en ? "Create a proposal" : "提案を作る",
    proposalTitle: en ? "Proposed changes" : "今回の変更案",
    proposalHelp: en
      ? "Only the changes below will be applied. The schedule is checked again when you apply it."
      : "下に表示された変更だけを反映します。反映時にも予定を再確認し、内容が変わった場合は反映しません。",
    before: en ? "Before" : "変更前",
    after: en ? "After" : "変更後",
    chooseAgain: en ? "Choose tasks again" : "選び直す",
    apply: en ? "Apply this proposal" : "この提案を反映する",
    undo: en ? "Undo this change" : "この変更を戻す",
    close: en ? "Close" : "閉じる",
    noPlans: en ? "There is no plan to adjust yet." : "調整できる計画がまだありません。",
    noCandidates: en ? "There are no unfinished weekly tasks in this month that can be adjusted." : "今月に調整できる未完了の週次タスクはありません。",
    noneSelected: en ? "Select at least one task to create a proposal." : "提案を作るには、移動してよいタスクを1件以上選んでください。",
    noChanges: en ? "There are no schedule changes to apply." : "反映できる日程変更はありません。",
    selected: (count: number) => en ? String(count) + " task(s) may move" : String(count) + "件を移動候補に選択中",
    previewOnly: en ? "Nothing changes until you press Apply." : "「反映」を押すまで、タスクの日程は変更されません。",
    applied: en ? "Applied. You can undo this adjustment here." : "反映しました。この画面から今回の予定調整を戻せます。",
    undoDone: en ? "Restored the state before this adjustment." : "予定を調整する前の状態に戻しました。",
    error: en ? "We could not complete the request. Please try again." : "処理を完了できませんでした。時間をおいてもう一度お試しください。",
    capacity: en ? "Weekly capacity" : "週の配置上限",
  };
}

function formatDates(dates: { planned_start: string | null; deadline: string | null } | null | undefined) {
  if (!dates?.planned_start || !dates?.deadline) return "-";
  return dates.planned_start + " 〜 " + dates.deadline;
}

function candidateDescription(candidate: RestartRouteCandidate, checked: boolean, copy: ReturnType<typeof copyFor>) {
  return formatDates(candidate.before) + "　・　" + (checked ? copy.movable : copy.fixed);
}

export default function RestartRoutePreview() {
  const { session } = useSessionContext();
  const { language } = useContext(LanguageContext) as { language: string };
  const copy = useMemo(() => copyFor(language), [language]);

  const [open, setOpen] = useState(false);
  const [plans, setPlans] = useState<AiPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [stage, setStage] = useState<Stage>("plans");
  const [candidatePreview, setCandidatePreview] = useState<RestartRoutePreviewData | null>(null);
  const [proposal, setProposal] = useState<RestartRoutePreviewData | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<number>>(new Set());
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [appliedRevisionId, setAppliedRevisionId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isBusy = isLoadingPlans || isLoadingPreview || isApplying || isUndoing;
  const candidates = candidatePreview?.feasibility?.candidates ?? [];
  const changes = proposal?.feasibility?.changes ?? [];
  const proposalReady = proposal?.feasibility?.status === "ready" && changes.length > 0;

  const emitTaskRefresh = () => {
    if (typeof window !== "undefined") window.dispatchEvent(new Event("restart-route-applied"));
  };

  const resetFlow = () => {
    setStage("plans");
    setCandidatePreview(null);
    setProposal(null);
    setSelectedTaskIds(new Set());
    setAppliedRevisionId(null);
    setNotice(null);
    setError(null);
  };

  const handleOpen = async () => {
    setOpen(true);
    resetFlow();
    if (!session?.access_token || plans.length > 0) return;

    setIsLoadingPlans(true);
    try {
      const loadedPlans = await getAiPlans(session.access_token, { hideGlobalLoader: true });
      setPlans(loadedPlans);
      setSelectedPlanId(loadedPlans[0]?.id ?? null);
    } catch (requestError) {
      console.error("RestartRoutePreview: plans request failed", requestError);
      setError(copy.error);
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const inspectCandidates = async () => {
    if (!session?.access_token || !selectedPlanId) return;

    setIsLoadingPreview(true);
    setError(null);
    setNotice(null);
    try {
      const response = await getRestartRoutePreview(session.access_token, {
        plan_id: selectedPlanId,
        task_ids: [],
      });
      const nextCandidates = response.preview.feasibility?.candidates ?? [];
      setCandidatePreview(response.preview);
      setProposal(null);
      setSelectedTaskIds(new Set(nextCandidates.map((task) => task.task_id)));
      setStage("select");
    } catch (requestError) {
      console.error("RestartRoutePreview: candidate request failed", requestError);
      setError(copy.error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const toggleTask = (taskId: number) => {
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const createProposal = async () => {
    if (!session?.access_token || !selectedPlanId || selectedTaskIds.size === 0) return;

    setIsLoadingPreview(true);
    setError(null);
    setNotice(null);
    try {
      const response = await getRestartRoutePreview(session.access_token, {
        plan_id: selectedPlanId,
        task_ids: Array.from(selectedTaskIds),
      });
      setProposal(response.preview);
      setStage("proposal");
    } catch (requestError) {
      console.error("RestartRoutePreview: proposal request failed", requestError);
      setError(copy.error);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const chooseAgain = () => {
    setProposal(null);
    setAppliedRevisionId(null);
    setNotice(null);
    setError(null);
    setStage("select");
  };

  const apply = async () => {
    if (!session?.access_token || !selectedPlanId || !proposal || !proposalReady) return;

    setIsApplying(true);
    setError(null);
    try {
      const response = await applyRestartRoute(session.access_token, {
        plan_id: selectedPlanId,
        summary: proposal.summary,
        task_ids: Array.from(selectedTaskIds),
        expected_changes: changes.map((change) => ({
          task_id: change.task_id,
          before: change.before,
          after: change.after as { planned_start: string; deadline: string },
        })),
      });
      setAppliedRevisionId(response.revision.id);
      setNotice(copy.applied);
      emitTaskRefresh();
    } catch (requestError) {
      console.error("RestartRoutePreview: apply failed", requestError);
      setError(copy.error);
    } finally {
      setIsApplying(false);
    }
  };

  const undo = async () => {
    if (isUndoing || !session?.access_token || !appliedRevisionId) return;

    setIsUndoing(true);
    setError(null);
    try {
      await undoRestartRoute(session.access_token, appliedRevisionId);
      setAppliedRevisionId(null);
      setNotice(copy.undoDone);
      emitTaskRefresh();
    } catch (requestError) {
      console.error("RestartRoutePreview: undo failed", requestError);
      setError(copy.error);
    } finally {
      setIsUndoing(false);
    }
  };

  return (
    <>
      <Card sx={{ px: 2, py: 1.5 }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={{ xs: 1.5, sm: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>{copy.title}</Typography>
            <Typography variant="body2" color="text.info" mt={0.25}>{copy.description}</Typography>
          </Box>
          <MDButton variant="gradient" color="info" onClick={handleOpen}>{copy.open}</MDButton>
        </Stack>
      </Card>

      <Dialog open={open} onClose={() => !isBusy && setOpen(false)} fullWidth maxWidth="md">
        <Box sx={{ position: "relative" }}>
          <DialogTitle>{copy.modalTitle}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.info">{copy.modalDescription}</Typography>

            {stage === "plans" && (
              <Stack spacing={2.5} mt={3}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>{copy.choosePlan}</Typography>
                  {isLoadingPlans ? (
                    <Box minHeight={120} />
                  ) : plans.length === 0 ? (
                    <Alert severity="info">{copy.noPlans}</Alert>
                  ) : (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {plans.map((plan) => (
                        <Chip
                          key={plan.id}
                          label={plan.title}
                          color={plan.id === selectedPlanId ? "info" : "default"}
                          variant={plan.id === selectedPlanId ? "filled" : "outlined"}
                          onClick={() => setSelectedPlanId(plan.id)}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
                <Box>
                  <MDButton variant="gradient" color="info" disabled={!selectedPlanId || isLoadingPlans} onClick={inspectCandidates}>
                    {copy.inspect}
                  </MDButton>
                </Box>
              </Stack>
            )}

            {stage === "select" && (
              <Stack spacing={2} mt={3}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>{copy.selectionTitle}</Typography>
                  <Typography variant="body2" color="text.info" mt={0.5}>{copy.selectionHelp}</Typography>
                </Box>
                {candidates.length === 0 ? (
                  <Alert severity="info">{copy.noCandidates}</Alert>
                ) : (
                  <Stack spacing={1}>
                    {candidates.map((candidate) => {
                      const checked = selectedTaskIds.has(candidate.task_id);
                      return (
                        <Box key={candidate.task_id} sx={{ border: "1px solid", borderColor: checked ? "info.light" : "divider", borderRadius: 1.5, px: 1 }}>
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleTask(candidate.task_id)}
                            inputProps={{ "aria-label": candidate.title }}
                          />
                          <Box component="span" sx={{ display: "inline-block", verticalAlign: "middle", py: 1, maxWidth: "calc(100% - 48px)" }}>
                            <Typography variant="body2" fontWeight={600}>{candidate.title}</Typography>
                            <Typography variant="caption" color="text.info">{candidateDescription(candidate, checked, copy)}</Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
                <Typography variant="body2" color="text.info" fontWeight={700}>{copy.selected(selectedTaskIds.size)}</Typography>
                {selectedTaskIds.size === 0 && <Alert severity="info">{copy.noneSelected}</Alert>}
                <Box>
                  <MDButton variant="gradient" color="info" disabled={selectedTaskIds.size === 0} onClick={createProposal}>
                    {copy.create}
                  </MDButton>
                </Box>
              </Stack>
            )}

            {stage === "proposal" && proposal && (
              <Stack spacing={2} mt={3}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>{copy.proposalTitle}</Typography>
                  <Typography variant="body2" color="text.info" mt={0.5}>{copy.proposalHelp}</Typography>
                </Box>
                <Alert severity="info">{copy.previewOnly}</Alert>
                {proposal.feasibility && (
                  <Alert severity={proposal.feasibility.status === "ready" ? "success" : "warning"}>
                    {proposal.feasibility.message}
                  </Alert>
                )}
                {changes.length === 0 ? (
                  <Alert severity="warning">{copy.noChanges}</Alert>
                ) : (
                  <Stack spacing={1}>
                    {changes.map((change: RestartRouteChange) => (
                      <Box key={change.task_id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
                        <Typography variant="body2" fontWeight={700}>{change.title}</Typography>
                        <Typography variant="caption" color="text.info" display="block" mt={0.5}>
                          {copy.before + ": " + formatDates(change.before)}
                        </Typography>
                        <Typography variant="caption" color="text.info" display="block">
                          {copy.after + ": " + formatDates(change.after)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <Button
                    variant="outlined"
                    onClick={chooseAgain}
                    sx={{
                      color: "#000000",
                      borderColor: "info.main",
                      "&:hover": {
                        color: "#000000",
                        borderColor: "info.dark",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    {copy.chooseAgain}
                  </Button>
                  <MDButton variant="gradient" color="info" disabled={!proposalReady || !!appliedRevisionId} onClick={apply}>
                    {copy.apply}
                  </MDButton>
                  {!!appliedRevisionId && (
                    <Button
                      variant="outlined"
                      onClick={undo}
                      sx={{
                        color: "#000000",
                        borderColor: "info.main",
                        "&:hover": {
                          color: "#000000",
                          borderColor: "info.dark",
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      {copy.undo}
                    </Button>
                  )}
                </Stack>
              </Stack>
            )}

            {!!error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}
            {!!notice && <Alert severity="success" sx={{ mt: 3 }}>{notice}</Alert>}
          </DialogContent>
          <DialogActions><Button disabled={isBusy} onClick={() => setOpen(false)}>{copy.close}</Button></DialogActions>
          {isBusy && (
            <Box role="status" aria-live="polite" sx={{ position: "absolute", inset: 0, zIndex: 2, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(255,255,255,0.48)", cursor: "wait" }}>
              <CircularProgress size={40} color="info" />
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
}
